import classNames from "classnames";
import { html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import {
  getDiagramVisual,
  getImageVisual,
  getLiteraryDesignUnitVisual,
  getMacroLiteraryDesignVisual,
  getTableVisual,
  getVideoVisual,
} from "../../data/visuals";
import { getRGBFromHex } from "../../utils/color";
import { toSec } from "../../utils/timecode";

import styles from "./slide.css";

import type { SlideData, SlideState, VisualData } from "../../types.d";
import type { PresentationTheme } from "../slide-presentation/slide-presentation";
import type { PropertyValues } from "lit";

const INIT_RETRY_BACKOFF = 10;
const RETRY_ATTEMPTS = 25;

const DEFAULT_PRIMARY = "#104366";
const DEFAULT_SECONDARY = "#e24213";

const ARC_ID_FIELDS = [
  "imgArcId",
  "diagramArcId",
  "tableArcId",
  "videoArcId",
  "literaryDesignArcId",
  "macroLiteraryDesignArcId",
];

export type SlideViewMode = "default" | "mobile" | "mobile-tall" | "tall";

@customElement("bp-slide")
export class Slide extends LitElement {
  static styles = styles;

  @property({ attribute: "current-time" })
  currentTime = 0;

  @property({ type: Object, attribute: "data" })
  data?: SlideData;

  @property({
    type: Boolean,
    attribute: "no-video",
    converter: (value) => (value === "true" ? true : false),
  })
  noVideo?: boolean = false;

  @property({
    type: Boolean,
    attribute: "paused",
    converter: (value) => (value === "true" ? true : false),
  })
  paused?: boolean = true;

  @property({ type: Number, attribute: "playback-rate" })
  playbackRate = 1;

  @property({ attribute: "primary-color" })
  primaryColor: string = DEFAULT_PRIMARY;

  @property({ attribute: "secondary-color" })
  secondaryColor: string = DEFAULT_SECONDARY;

  @property({ type: Object, attribute: "slide-state" })
  slideState?: SlideState;

  @state()
  store: Map<string, VisualData> = new Map([]);

  @property({ type: Object })
  theme?: PresentationTheme;

  @property({ attribute: "view-mode" })
  viewMode: SlideViewMode = "default";

  get styles() {
    if (!this.data) return ``;
    const { red, green, blue } =
      this.data?.variant == "main-point"
        ? getRGBFromHex(this.primaryColor)
        : this.data?.variant == "word-definition"
          ? getRGBFromHex(this.secondaryColor)
          : getRGBFromHex("#fff");

    return [
      `--slide-background-red: ${red};`,
      `--slide-background-green: ${green};`,
      `--slide-background-blue: ${blue};`,
    ].join(" ");
  }

  render() {
    return html`
      <div style="${this.styles}" class="${classNames("slide", this.viewMode)}">
        ${this.renderSlide()}
      </div>
    `;
  }

  updated(changedProperties: PropertyValues) {
    if (!changedProperties.has("data")) return;

    // Fetch all of the data required to render the slide.
    for (const field of ARC_ID_FIELDS) {
      const arcId = this.data?.[field as keyof SlideData] as string | undefined;

      if (!arcId || this.store.get(arcId)) continue;

      this.getData(field, arcId)
        .then(({ data }) => {
          this.store.set(arcId, data);
          this.requestUpdate();
        })
        .catch(() => {
          this.retryFetch(arcId, field);
        });
    }
  }

  private async getData(arcIdField: string, arcId: string) {
    switch (arcIdField) {
      case "diagramArcId":
        return getDiagramVisual(arcId);
      case "literaryDesignArcId":
        return getLiteraryDesignUnitVisual(arcId);
      case "macroLiteraryDesignArcId":
        return getMacroLiteraryDesignVisual(arcId);
      case "tableArcId":
        return getTableVisual(arcId);
      case "videoArcId":
        return getVideoVisual(arcId);
      default:
        return getImageVisual(arcId);
    }
  }

  private renderSlide() {
    if (!this.data) return html``;
    const hasTitle: boolean = !this.data?.title || this.data.title.length === 0;
    let variant = this.data!.variant;

    if (variant == "image" && this.slideState) {
      variant = "diagram";
    }

    switch (variant) {
      case "diagram":
        return html`
          <bp-diagram-slide
            view-mode="${this.viewMode}"
            .slideState="${this.slideState}"
            ?hide-title=${hasTitle}
            ?title-wraps-2-lines=${this.data?.titleWraps2Lines}
            ?hide-shadow=${this.data?.hideShadow}
            .data=${this.store.get(this.data?.diagramArcId ?? "") ||
            this.store.get(this.data?.imgArcId ?? "")}
          >
            <div slot="title">${unsafeHTML(this.data?.title)}</div>
          </bp-diagram-slide>
        `;
      case "ephesians-literary-design":
        return html`
          <bp-ephesians-literary-design-slide
            view-mode="${this.viewMode}"
            ?hide-title=${hasTitle}
            ?title-wraps-2-lines=${this.data?.titleWraps2Lines}
            .slideState="${this.slideState}"
            .data=${this.store.get(this.data?.literaryDesignArcId ?? "")}
          >
            <div slot="title">${unsafeHTML(this.data?.title)}</div>
          </bp-ephesians-literary-design-slide>
        `;
      case "illustration":
      case "screen-recording":
        return html`
          <bp-video-slide
            view-mode="${this.viewMode}"
            current-time="${toSec(this.currentTime)}"
            ?paused=${this.paused}
            ?no-video=${this.noVideo}
            playback-rate="${this.playbackRate}"
            .data=${this.store.get(this.data?.videoArcId ?? "")}
          />
        `;
      case "image":
        return html`
          <bp-image-slide
            view-mode="${this.viewMode}"
            title="${this.data?.title}"
            .data=${this.store.get(this.data?.diagramArcId ?? "") ||
            this.store.get(this.data?.imgArcId ?? "")}
          >
          </bp-image-slide>
        `;
      case "literary-design":
        return html`
          <bp-literary-design-slide
            view-mode="${this.viewMode}"
            ?hide-title=${hasTitle}
            ?title-wraps-2-lines=${this.data?.titleWraps2Lines}
            .slideState="${this.slideState}"
            excluded-key-paths="${JSON.stringify(
              this.data?.excludedKeyPaths || [],
            )}"
            .data=${this.store.get(this.data?.literaryDesignArcId ?? "")}
          >
            <div slot="title">${unsafeHTML(this.data?.title)}</div>
          </bp-literary-design-slide>
        `;
      case "macro-literary-design":
        return html`
          <bp-macro-literary-design-slide
            view-mode="${this.viewMode}"
            ?hide-title=${hasTitle}
            ?title-wraps-2-lines=${this.data?.titleWraps2Lines}
            .slideState="${this.slideState}"
            .data=${this.store.get(this.data?.macroLiteraryDesignArcId ?? "")}
          >
            <div slot="title">${unsafeHTML(this.data?.title)}</div>
          </bp-macro-literary-design-slide>
        `;
      case "main-point":
        return html`
          <bp-main-point-slide view-mode="${this.viewMode}">
            <div>${unsafeHTML(this.data?.content)}</div>
          </bp-main-point-slide>
        `;
      case "multi-verse":
        return html`
          <bp-multi-verse-slide
            view-mode="${this.viewMode}"
            title="${this.data?.title}"
            .multiVerseData=${this.data?.multiVerseData}
            .slideState="${this.slideState}"
          />
        `;
      case "paragraph":
        return html`
          <bp-paragraph-slide
            view-mode="${this.viewMode}"
            title="${this.data?.title}"
            content="${this.data?.content}"
            caption="${this.data?.caption}"
            .slideState="${this.slideState}"
          />
        `;
      case "question":
        return html` <bp-question-slide view-mode="${this.viewMode}">
          <div>${unsafeHTML(this.data?.content)}</div>
        </bp-question-slide>`;
      case "resource":
        return html`
          <bp-resource-slide
            view-mode="${this.viewMode}"
            .authors=${this.data?.authors}
          >
            <div>${unsafeHTML(this.data?.title)}</div>
          </bp-resource-slide>
        `;
      case "single-verse":
        return html`
          <bp-single-verse-slide
            view-mode="${this.viewMode}"
            .verseData=${this.data?.verseData}
            .slideState="${this.slideState}"
          />
        `;
      case "table":
        return html`
          <bp-table-slide
            view-mode="${this.viewMode}"
            .slideState="${this.slideState}"
            ?hide-title=${hasTitle}
            ?title-wraps-2-lines=${this.data?.titleWraps2Lines}
            .data=${this.store.get(this.data?.tableArcId ?? "")}
          >
            <div slot="title">${unsafeHTML(this.data?.title)}</div>
          </bp-table-slide>
        `;
      case "thank-you":
        return html`<bp-thank-you-slide view-mode="${this.viewMode}" />`;
      case "title":
        return html`
          <bp-title-slide
            view-mode="${this.viewMode}"
            icon-src="${this.theme?.artwork?.class}"
            artwork-src="${this.theme?.artwork?.module}"
          >
            <div slot="session-number">${this.data?.sessionNumber}</div>
            <div slot="session-name">${unsafeHTML(this.data?.sessionName)}</div>
            <div slot="class-name">${this.data?.className}</div>
            <div slot="teacher-name">${this.data?.teacherName}</div>
          </bp-title-slide>
        `;
      case "verse-comparison":
        return html`
          <bp-verse-comparison-slide
            view-mode="${this.viewMode}"
            title="${this.data?.title}"
            .multiVerseData=${this.data?.multiVerseData}
            .slideState="${this.slideState}"
          />
        `;
      case "word-definition":
        return html`
          <bp-word-definition-slide
            view-mode="${this.viewMode}"
            .wordDefinitionData="${this.data?.wordDefinitionData}"
            .slideState="${this.slideState}"
          >
          </bp-word-definition-slide>
        `;
      default:
        return html``;
    }
  }

  private retryFetch(arcId: string, arcIdField: string, numReattempts = 0) {
    if (numReattempts === RETRY_ATTEMPTS) return;
    const backoff = INIT_RETRY_BACKOFF * Math.pow(2, numReattempts);
    setTimeout(() => {
      this.getData(arcIdField, arcId)
        .then(({ data }) => {
          this.store.set(arcId, data);
          this.requestUpdate();
        })
        .catch(() => this.retryFetch(arcId, arcIdField, numReattempts + 1));
    }, backoff);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-slide": Slide;
  }
}
